import { useCallback, useEffect } from 'react'
import type { SWRResponse, Middleware, SWRHook, Fetcher, SWRConfiguration, Key, BareFetcher } from 'swr'
import useSwr, { mutate as swrMutate } from 'swr'
import useSWRInfinite, {
  type SWRInfiniteKeyLoader,
  type SWRInfiniteResponse,
  type SWRInfiniteHook,
  type SWRInfiniteConfiguration,
} from 'swr/infinite'
import type { AxiosError } from 'axios'
import axiosStatic from 'axios'
import axiosInstance, { type AxiosRequestConfig, retryOnErrors } from '@fs/zion-axios'
import type Cache from '@fs/zion-cache'
import { useTranslation } from 'react-i18next'
import { type TFunction } from 'i18next'
import { useErrorOverlay } from '../hooks/useDataStatusOverlay'
import removeNulls from './removeNulls'

const axios = axiosInstance

if (axios.defaults) {
  // To simplify mocking for tests check that defaults exists before adding the common accept header. This makes it so that we always get json back from our requests. Imperva would give us html back if we accepted anything.
  // We are trying to make it so that imperva will just return json if application/json has priority in the accept header: https://familysearch.slack.com/archives/C0912BDBL/p1702505979574829?thread_ts=1701709979.708849&cid=C0912BDBL
  axios.defaults.headers.common.accept = 'application/json'
}
retryOnErrors?.(axios) // To simplify mocking for tests, only call if it exists

/** Converts data -> dataName and mutate -> mutateDataName for SwrResponse */
export type SwrCustomizedResponse<T, DataName extends string> = Omit<SwrResponse<T>, 'data' | 'mutate'> &
  Record<DataName, SwrResponse<T>['data']> &
  Record<`mutate${Capitalize<DataName>}`, SwrResponse<T>['mutate']>

export type Transform<In, Out = In> = (data: In) => Out

const MODE_STANDARD = 'standard'
const MODE_INFINITE = 'infinite'
export type RequestMode = typeof MODE_STANDARD | typeof MODE_INFINITE

type BaseSwrOptions<Out, In = Out> = {
  transform?: Transform<In, Out>
  emulator?: (url: string, zionCache?: Cache<CacheData<In>>) => Promise<Out | undefined>
  errorMessage?: string
  requestOptions?: AxiosRequestConfig
}

export type SwrOptions<Out, In = Out> = SWRConfiguration<Out> & BaseSwrOptions<Out, In>

export type SwrOptionsInfinite<Out, In = Out> = SWRInfiniteConfiguration<Out> & BaseSwrOptions<Out, In>

export type SwrResponse<T> = SWRResponse<T, AxiosError>

type CacheData<T> = [string, T]

export async function fetcher<T>(
  url: string,
  zionCache: Cache<CacheData<T>> | undefined,
  requestOptions: AxiosRequestConfig = {}
): Promise<T | undefined> {
  const cachedData = zionCache && url ? await zionCache.getItem(url) : undefined
  const { headers } = requestOptions
  const etag = cachedData?.[0]
  const etagHeader: { 'If-None-Match'?: string } = etag ? { 'If-None-Match': etag } : {}
  const res = await axios.get(url, {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
    timeout: 30000,
    ...requestOptions,
    headers: { ...etagHeader, ...headers },
  })

  if (res.headers['retry-after']) {
    const retryAfter = parseInt(res.headers['retry-after'], 10)
    setTimeout(() => swrMutate(url), retryAfter * 1000)
  }

  if (zionCache && url && res.headers.etag && res.headers.etag !== etag) {
    // No need to await because if it fails we'll just remake the request
    zionCache.setItem(url, [res.headers.etag, res.data])
  }

  return res.status === 304 ? cachedData?.[1] : res.data
}

/**
 * Returns SWR middleware that converts data read by fetcher informat `In` to format `Out`
 * using the provided `cvt` function.
 *
 * This is built as a middleware so it will work with fallbackData appropriately. When fallbackData is provided,
 * then the middleware does not run. If it did run, we would convert already converted data (assuming they pass in converted data).
 */
const converter = <In, Out>(cvt: Transform<In, Out>, mode: RequestMode) => {
  return (useSWRNext: typeof mode extends 'infinite' ? SWRInfiniteHook : SWRHook) =>
    (
      key: Key,
      _fetcher: BareFetcher<In> | null,
      config: typeof mode extends 'infinite' ? SwrOptionsInfinite<Out, In> : SwrOptions<Out, In>
    ) => {
      async function cvtFetcher(...args: unknown[]): Promise<Out> {
        if (!_fetcher) throw new Error(`Unexpected null fetcher`)
        const fetched = await _fetcher(...args)
        return cvt(fetched)
      }
      return useSWRNext(key, cvtFetcher, config)
    }
}

const preloaded = {}

/**
 * Uses SWR to make a request.
 * Returns the entire response.
 */
export function useSwrRequest<Out, In = Out>(
  url: string | null,
  zionCache?: Cache<CacheData<In>>,
  swrOptions?: SwrOptions<Out, In>
): SwrResponse<Out> {
  const [t] = useTranslation()
  const showError = useErrorOverlay()
  const { transform, emulator, errorMessage, requestOptions, ...options } = swrOptions || {}

  const swrFetcher = useCallback(async () => {
    if (!url) return null
    try {
      if (emulator) return await emulator(url, zionCache)
      return await fetcher?.(url, zionCache, requestOptions)
    } catch (e) {
      if (axiosStatic.isAxiosError(e) && e.response?.status && e.response.status >= 500) {
        showError(errorMessage ?? t('tree-person.server.error'), e)
        return null
      }
      throw e
    }
  }, [emulator, url, zionCache, requestOptions, showError, errorMessage, t]) as Fetcher<Out>

  const middlewares = getMiddlewares(MODE_STANDARD, swrOptions)

  const response = useSwr<Out, AxiosError>(url, swrFetcher, {
    ...options,
    shouldRetryOnError: false, // retryOnErrors from zion-axios handles retrying on errors
    use: middlewares,
  })

  const { data, mutate, isValidating } = response

  // Preload the data into the SWR cache if it isn't already there, but it is in the persistent cache
  useEffect(() => {
    const preload = async (): Promise<void> => {
      if (!url) return
      if (!preloaded[url]) {
        preloaded[url] = true
        if (url && !data && zionCache && !isValidating) {
          const cachedData = await zionCache.getItem(url)
          if (cachedData) {
            const toMutate = !transform ? (cachedData[1] as unknown as Out) : transform(cachedData[1])
            mutate(toMutate, false)
          }
        }
      }
    }
    preload()
  }, [zionCache, data, url, transform, mutate, isValidating])

  // Can't have a flag to either use the middleware or not, since whatever gets run through gets saved to cache
  // Can't make the key passed to SWR different based on whether or not it's transformed since mutating won't work if we call from global mutate
  // Need to be able to get the original data via preloading

  return response
}

type SwrInfiniteFetcherArgs<Out> = {
  url: string | null
  emulator?: (url: string, zionCache?: Cache<CacheData<Out>>) => Promise<Out | undefined>
  zionCache?: Cache<CacheData<Out>>
  requestOptions?: AxiosRequestConfig
  showError: ReturnType<typeof useErrorOverlay>
  errorMessage?: string
  t: TFunction
}

async function swrInfiniteFetcher<Out>({
  url,
  emulator,
  zionCache,
  requestOptions,
  showError,
  errorMessage,
  t,
}: SwrInfiniteFetcherArgs<Out>): Promise<Out> {
  if (!url || typeof url !== 'string') {
    throw new Error('Invalid URL for swrInfiniteFetcher')
  }
  try {
    if (emulator) {
      const result = await emulator(url, zionCache)
      if (result === undefined || result === null) {
        throw new Error('Emulator returned null or undefined')
      }
      return result as Out
    }
    const result = await fetcher?.(url, zionCache, requestOptions)
    if (result === undefined || result === null) {
      throw new Error('Fetcher returned null or undefined')
    }
    return result as Out
  } catch (e) {
    if (axiosStatic.isAxiosError(e) && e.response?.status && e.response.status >= 500) {
      showError(errorMessage ?? t('tree-person.server.error'), e)
      throw e
    }
    throw e
  }
}

/**
 * Uses SWR Infinite to make a request.
 * Returns the entire response.
 */
export function useSwrInfiniteRequest<Out, In = Out>(
  getKey: SWRInfiniteKeyLoader<Out>,
  zionCache?: Cache<CacheData<Out>>,
  swrOptions?: SwrOptionsInfinite<Out, In>
): SWRInfiniteResponse<Out> {
  const [t] = useTranslation()
  const showError = useErrorOverlay()
  const { emulator, errorMessage, requestOptions, ...options } = swrOptions || {}
  const middlewares = getMiddlewares(MODE_INFINITE, swrOptions)

  const response = useSWRInfinite<Out, AxiosError, SWRInfiniteKeyLoader<Out>>(
    getKey,
    (url) =>
      swrInfiniteFetcher<Out>({
        url: typeof url === 'string' ? url : null,
        emulator,
        zionCache,
        requestOptions,
        showError,
        errorMessage,
        t,
      }),
    {
      ...options,
      shouldRetryOnError: false, // retryOnErrors from zion-axios handles retrying on errors
      use: middlewares,
    }
  )

  return response
}

function getMiddlewares<T, In>(
  mode: RequestMode,
  options?: SwrOptions<T, In> | SwrOptionsInfinite<T, In>
): Middleware[] {
  const middlewares = [converter<In, T>(removeNulls, mode)] as Middleware[]

  if (!options) return middlewares

  const { transform, use } = options

  // Create new array to prevent reference manipulation
  if (use) middlewares.push(...use)

  if (transform) middlewares.push(converter(transform, mode) as Middleware)
  return middlewares
}

/**
 * Issues a request that will update a resource and marks the SWR cache as stale.
 * The update is performed in a way that is "optimistic". That is, it assumes the update
 * will succeed, so it writes `newData` to the SWR cache, then issues the request, then
 * marks the SWR cache as stale so it will be refetched from the backend.
 * @param key - The SWR cache key.
 * @param newData - The data to write (temporarily) into SWR. This must be in the cache format.
 *                  Pass `undefined` to disable optimistic update of the SWR cache.
 * @param updater - A function that issues the backend request. It will be `await`ed.
 * @returns {Promise<T>} - The return value from `updater`.
 */
export async function updateRequest<T = unknown>(
  key: string,
  newData: unknown | undefined,
  updater: () => Promise<T>,
  shouldRevalidate = true
): Promise<T> {
  return updateRequestMultiple([{ key, newData }], updater, shouldRevalidate)
}

export async function updateRequestMultiple<T = unknown>(
  keys: (string | { key: string; newData?: unknown })[],
  updater: () => Promise<T>,
  shouldRevalidate = true
): Promise<T> {
  function doMutate(revalidate = true): void {
    keys.forEach((k) => {
      if (revalidate) {
        if (typeof k === 'string') {
          swrMutate(k)
        } else {
          swrMutate(k.key)
        }
      } else if (typeof k !== 'string' && k.newData) {
        swrMutate(k.key, k.newData, false)
      }
    })
  }
  try {
    doMutate(false)
    const result = await updater()
    if (shouldRevalidate) doMutate()
    return result
  } catch (error) {
    console.error('updater threw', error)
    // Force SWR to re-read original data since the update failed
    doMutate()
    throw error
  }
}
